import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { T } from "@/lib/testIds";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Upload, Search, Send, CheckCircle2, Trash2, ArrowRight, Link2, Tag, Download, AlertTriangle, MessageSquare, Copy, ExternalLink, MessageCircle } from "lucide-react";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));

const statusPillClass = (s) =>
  s === "paid" ? "co-pill co-pill-paid" : s === "overdue" ? "co-pill co-pill-overdue" : "co-pill co-pill-pending";

const emptyForm = {
  name: "",
  phone: "",
  email: "",
  product_name: "",
  amount: "",
  billing_cycle: "monthly",
  next_due_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
  notes: "",
  segments: "",
  payment_option_ids: [],
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [segments, setSegments] = useState([]);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [segFilter, setSegFilter] = useState("all");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [importReport, setImportReport] = useState(null); // {inserted, errors, failed_rows}
  const [smsDialogClient, setSmsDialogClient] = useState(null);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [smsPreview, setSmsPreview] = useState({}); // {template_id: data}
  const [smsSelectedTpl, setSmsSelectedTpl] = useState(null);
  const [popClient, setPopClient] = useState(null); // client whose payment options are being edited
  const [popSelected, setPopSelected] = useState([]); // ids chosen in picker
  const [popSaving, setPopSaving] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status !== "all") params.status = status;
      if (segFilter !== "all") params.segment = segFilter;
      const [r, sg, po] = await Promise.all([
        api.get("/clients", { params }),
        api.get("/clients/segments/list"),
        api.get("/payment-options"),
      ]);
      setClients(r.data);
      setSegments(sg.data);
      setPaymentOptions(po.data);
    } catch (e) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, status, segFilter]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const segArr = (form.segments || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      await api.post("/clients", {
        ...form,
        amount: parseFloat(form.amount),
        segments: segArr,
        payment_option_ids: form.payment_option_ids || [],
      });
      toast.success("Client added");
      setAddOpen(false);
      setForm(emptyForm);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    } finally {
      setSaving(false);
    }
  };

  const copyPortalLink = async (portalToken) => {
    const url = `${window.location.origin}/portal/${portalToken}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Portal link copied to clipboard");
    } catch {
      toast.info(url);
    }
  };

  const openSmsPicker = async (client) => {
    setSmsDialogClient(client);
    setSmsSelectedTpl(null);
    setSmsPreview({});
    try {
      let list = smsTemplates;
      if (list.length === 0) {
        const t = await api.get("/sms-templates");
        list = t.data || [];
        setSmsTemplates(list);
      }
      const results = await Promise.all(
        list.map((t) =>
          api
            .post("/sms-templates/render", { template_id: t.id, client_id: client.id })
            .then((r) => [t.id, r.data])
            .catch(() => [t.id, null])
        )
      );
      const map = {};
      results.forEach(([id, d]) => { if (d) map[id] = d; });
      setSmsPreview(map);
      if (list.length > 0) setSmsSelectedTpl(list[0].id);
    } catch {
      toast.error("Failed to load SMS templates");
    }
  };

  const copySmsText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("SMS copied — paste into your Messages app");
    } catch {
      toast.info("Copy failed — long-press and select the text");
    }
  };

  const openInSmsApp = (client, text) => {
    if (!client?.phone) {
      toast.error("Client has no phone number");
      return;
    }
    const phone = client.phone.replace(/\s/g, "");
    const body = encodeURIComponent(text);
    window.location.href = `sms:${phone}?body=${body}`;
  };

  const openWhatsAppQuick = async (client) => {
    try {
      const r = await api.get(`/clients/${client.id}/whatsapp-preview`);
      const url = r.data?.wa_url;
      if (!url) {
        toast.error("Could not build WhatsApp link");
        return;
      }
      // Opens WhatsApp Web on desktop, WhatsApp app on mobile
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to open WhatsApp");
    }
  };

  const openPaymentOptionsPicker = (client) => {
    setPopClient(client);
    setPopSelected(client.payment_option_ids || []);
  };

  const togglePopOption = (id, checked) => {
    setPopSelected((prev) => {
      const s = new Set(prev);
      if (checked) s.add(id); else s.delete(id);
      return Array.from(s);
    });
  };

  const savePaymentOptions = async () => {
    if (!popClient) return;
    setPopSaving(true);
    try {
      const r = await api.patch(`/clients/${popClient.id}`, { payment_option_ids: popSelected });
      setClients((cs) => cs.map((c) => (c.id === popClient.id ? r.data : c)));
      toast.success(popSelected.length ? "Payment options updated for this client" : "Cleared — this client will now see all active options");
      setPopClient(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setPopSaving(false);
    }
  };

  const sendReminder = async (id) => {
    try {
      const r = await api.post(`/reminders/send/${id}`);
      toast.success(`Sent ${r.data.count} reminder(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    }
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/clients/${id}/mark-paid`, { method: "manual" });
      toast.success("Marked as paid");
      await load();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const removeClient = async (id) => {
    if (!window.confirm("Delete this client? This cannot be undone.")) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client deleted");
      await load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const onUploadCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/clients/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { inserted, errors } = r.data;
      if (errors.length === 0) {
        toast.success(`Imported ${inserted} clients — no errors`);
      } else {
        toast.info(`Imported ${inserted} · ${errors.length} skipped — review the report`);
      }
      setImportReport(r.data);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const downloadFailedRows = () => {
    if (!importReport?.failed_rows?.length) return;
    const headers = ["_row", "_error", "name", "phone", "email", "product_name",
                     "amount", "billing_cycle", "next_due_date", "notes", "segments"];
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = importReport.failed_rows.map((r) =>
      headers.map((h) => escape(r[h] ?? "")).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "collectone-failed-rows.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded failed rows — fix and re-upload");
  };

  const downloadTemplate = () => {
    const csv = "name,phone,email,product_name,amount,billing_cycle,next_due_date,notes,segments\n"
      + "Rahul Kumar,+91 98123 45678,rahul@example.com,Premium Plan,\"₹ 4,999\",monthly,15-03-2026,VIP client,\"vip,q1\"\n"
      + "Priya Menon,9888888888,,Class 10 Coaching,3500,quarterly,01-04-2026,,\"grade-10\"\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "collectone-clients-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <header className="co-glass-header sticky top-0 z-20 px-6 lg:px-10 py-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="co-label">Directory</div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight mt-1">Clients</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={downloadTemplate}
            data-testid={T.csvTemplateBtn}
            className="text-xs font-semibold text-[#4338CA] hover:text-[#3730A3] px-2 py-2"
          >
            Download CSV template
          </button>
          <input ref={fileRef} type="file" accept=".csv" hidden onChange={onUploadCSV} data-testid={T.csvFileInput} />
          <button
            data-testid={T.bulkUploadBtn}
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 border border-[#0A0A0B] px-4 py-2 text-sm font-semibold rounded-sm hover:bg-[#F4F4F5] co-btn-press"
          >
            <Upload className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" /> Bulk CSV
          </button>
          <button
            data-testid={T.addClientBtn}
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 bg-[#4338CA] hover:bg-[#3730A3] text-white px-4 py-2 text-sm font-semibold rounded-sm co-btn-press"
          >
            <Plus className="h-4 w-4" strokeWidth={2} aria-hidden="true" /> Add client
          </button>
        </div>
      </header>

      <div className="p-6 lg:p-10">
        {/* Filters */}
        <div className="border co-hairline bg-white p-4 flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" strokeWidth={1.75} aria-hidden="true" />
            <Input
              data-testid={T.clientSearch}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, product, phone…"
              className="pl-9 h-10"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[180px] h-10" data-testid={T.clientStatusFilter}>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
          <Select value={segFilter} onValueChange={setSegFilter}>
            <SelectTrigger className="w-[200px] h-10" data-testid={T.clientSegmentFilter}>
              <SelectValue placeholder="Segment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All segments</SelectItem>
              {segments.map((s) => (
                <SelectItem key={s.segment} value={s.segment}>
                  {s.segment} · {s.count}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="border co-hairline bg-white overflow-x-auto">
          <table className="co-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Product</th>
                <th>Amount</th>
                <th>Cycle</th>
                <th>Next due</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center text-[#52525B]">Loading…</td></tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="py-10 text-center">
                      <div className="text-sm text-[#52525B]">No clients yet.</div>
                      <button
                        onClick={() => setAddOpen(true)}
                        className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#4338CA]"
                      >
                        Add your first client <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                clients.map((c) => (
                  <tr key={c.id} data-testid={T.clientRow}>
                    <td>
                      <Link
                        to={`/app/clients/${c.id}`}
                        className="font-semibold text-[#0A0A0B] hover:text-[#4338CA]"
                      >
                        {c.name}
                      </Link>
                      <div className="text-xs text-[#52525B]">{c.phone}{c.email ? ` · ${c.email}` : ""}</div>
                      {(c.segments || []).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {c.segments.map((s) => (
                            <span key={s} className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 border border-[#C7D2FE] bg-[#ECECFE] text-[#4338CA] rounded-sm">
                              <Tag className="h-2.5 w-2.5" strokeWidth={2} />
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                      {(c.payment_option_ids || []).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {c.payment_option_ids.map((oid) => {
                            const opt = paymentOptions.find((p) => p.id === oid);
                            if (!opt) return null;
                            return (
                              <span
                                key={oid}
                                data-testid={T.clientMappedOption}
                                className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 border border-[#FDE68A] bg-[#FEF3C7] text-[#92400E] rounded-sm"
                                title={opt.type}
                              >
                                <Link2 className="h-2.5 w-2.5" strokeWidth={2} />
                                {opt.label}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td>{c.product_name}</td>
                    <td className="font-mono">{fmtINR(c.amount)}</td>
                    <td className="capitalize text-[#52525B]">{c.billing_cycle}</td>
                    <td className="font-mono">{c.next_due_date}</td>
                    <td><span className={statusPillClass(c.payment_status)}>{c.payment_status.toUpperCase()}</span></td>
                    <td>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          title="Copy portal link"
                          data-testid={`${T.copyPortalLink}-${c.id}`}
                          onClick={() => copyPortalLink(c.portal_token)}
                          className="p-2 hover:bg-[#F4F4F5] rounded-sm transition-colors duration-200"
                        >
                          <Link2 className="h-4 w-4 text-[#52525B]" strokeWidth={1.75} aria-hidden="true" />
                        </button>
                        <button
                          title="Copy SMS from a template"
                          data-testid={`${T.smsQuickCopyBtn}-${c.id}`}
                          onClick={() => openSmsPicker(c)}
                          className="p-2 hover:bg-[#F4F4F5] rounded-sm transition-colors duration-200"
                        >
                          <MessageSquare className="h-4 w-4 text-[#52525B]" strokeWidth={1.75} aria-hidden="true" />
                        </button>
                        <button
                          title="Send via WhatsApp (opens wa.me)"
                          data-testid={`${T.whatsappQuickBtn}-${c.id}`}
                          onClick={() => openWhatsAppQuick(c)}
                          className="p-2 hover:bg-[#F0FDF4] rounded-sm transition-colors duration-200"
                        >
                          <MessageCircle className="h-4 w-4 text-[#16A34A]" strokeWidth={1.75} aria-hidden="true" />
                        </button>
                        <button
                          title="Choose which payment option this client sees"
                          data-testid={`${T.paymentOptionsQuickBtn}-${c.id}`}
                          onClick={() => openPaymentOptionsPicker(c)}
                          className="p-2 hover:bg-[#F4F4F5] rounded-sm transition-colors duration-200"
                        >
                          <Tag className="h-4 w-4 text-[#52525B]" strokeWidth={1.75} aria-hidden="true" />
                        </button>
                        <button
                          title="Send reminder"
                          data-testid={`${T.sendReminderBtn}-${c.id}`}
                          onClick={() => sendReminder(c.id)}
                          className="p-2 hover:bg-[#F4F4F5] rounded-sm transition-colors duration-200"
                        >
                          <Send className="h-4 w-4 text-[#4338CA]" strokeWidth={1.75} aria-hidden="true" />
                        </button>
                        {c.payment_status !== "paid" && (
                          <button
                            title="Mark paid"
                            data-testid={`${T.markPaidBtn}-${c.id}`}
                            onClick={() => markPaid(c.id)}
                            className="p-2 hover:bg-[#ECFDF5] rounded-sm transition-colors duration-200"
                          >
                            <CheckCircle2 className="h-4 w-4 text-[#15803D]" strokeWidth={1.75} aria-hidden="true" />
                          </button>
                        )}
                        <button
                          title="Delete"
                          data-testid={`${T.deleteClientBtn}-${c.id}`}
                          onClick={() => removeClient(c.id)}
                          className="p-2 hover:bg-[#FEF2F2] rounded-sm transition-colors duration-200"
                        >
                          <Trash2 className="h-4 w-4 text-[#B91C1C]" strokeWidth={1.75} aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Add client</DialogTitle>
            <DialogDescription className="text-sm text-[#52525B]">
              Create a single client with their product, amount and next due date.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-1">
              <Label className="co-label">Name</Label>
              <Input data-testid={T.clientFormName} value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-2" />
            </div>
            <div>
              <Label className="co-label">Phone</Label>
              <Input data-testid={T.clientFormPhone} value={form.phone} onChange={(e) => set("phone", e.target.value)} required className="mt-2" placeholder="+91…" />
            </div>
            <div className="sm:col-span-2">
              <Label className="co-label">Email (optional)</Label>
              <Input data-testid={T.clientFormEmail} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label className="co-label">Product / plan</Label>
              <Input data-testid={T.clientFormProduct} value={form.product_name} onChange={(e) => set("product_name", e.target.value)} required className="mt-2" />
            </div>
            <div>
              <Label className="co-label">Amount (INR)</Label>
              <Input data-testid={T.clientFormAmount} type="number" min="1" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} required className="mt-2" />
            </div>
            <div>
              <Label className="co-label">Billing cycle</Label>
              <Select value={form.billing_cycle} onValueChange={(v) => set("billing_cycle", v)}>
                <SelectTrigger className="mt-2" data-testid={T.clientFormCycle}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="co-label">Next due date</Label>
              <Input data-testid={T.clientFormDue} type="date" value={form.next_due_date} onChange={(e) => set("next_due_date", e.target.value)} required className="mt-2" />
            </div>
            <div className="sm:col-span-2">
              <Label className="co-label">Segments (comma-separated tags)</Label>
              <Input
                data-testid={T.clientSegmentInput}
                value={form.segments}
                onChange={(e) => set("segments", e.target.value)}
                className="mt-2"
                placeholder="e.g. gym-premium, vip"
              />
              <div className="text-xs text-[#52525B] mt-1">
                Use segments to target different reminder rules at different cohorts.
              </div>
            </div>
            {paymentOptions.length > 0 && (
              <div className="sm:col-span-2">
                <Label className="co-label">Payment options for this client</Label>
                <div className="mt-2 border co-hairline bg-white p-3 space-y-2">
                  {paymentOptions.map((opt) => {
                    const checked = (form.payment_option_ids || []).includes(opt.id);
                    return (
                      <label
                        key={opt.id}
                        className="flex items-center gap-3 cursor-pointer hover:bg-[#FAFAFA] p-1 rounded-sm"
                      >
                        <input
                          type="checkbox"
                          data-testid={`${T.clientPaymentOptionCheckbox}-${opt.id}`}
                          checked={checked}
                          onChange={(e) => {
                            const ids = new Set(form.payment_option_ids || []);
                            if (e.target.checked) ids.add(opt.id);
                            else ids.delete(opt.id);
                            set("payment_option_ids", Array.from(ids));
                          }}
                          className="h-4 w-4 accent-[#4338CA]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold">{opt.label}</div>
                          <div className="text-[10px] text-[#52525B] truncate">
                            {opt.type === "directory" && (opt.config?.provider_name || "")}
                            {opt.type === "external" && (opt.config?.url || "")}
                            {opt.type === "upi" && `UPI · ${opt.config?.upi_id || ""}`}
                            {opt.type === "razorpay" && "Razorpay"}
                            {!opt.active && " · inactive"}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div className="text-xs text-[#52525B] mt-1">
                  Leave all unchecked to show every active option to this client (default).
                  Tick specific ones to restrict — e.g. an LIC client sees only the LIC link.
                </div>
              </div>
            )}
            <div className="sm:col-span-2">
              <Label className="co-label">Notes</Label>
              <Textarea data-testid={T.clientFormNotes} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-2" rows={2} />
            </div>
            <DialogFooter className="sm:col-span-2">
              <button type="button" onClick={() => setAddOpen(false)} className="px-4 py-2 text-sm font-semibold border border-[#0A0A0B] rounded-sm co-btn-press hover:bg-[#F4F4F5]">
                Cancel
              </button>
              <button data-testid={T.clientFormSubmit} type="submit" disabled={saving} className="px-4 py-2 text-sm font-semibold bg-[#4338CA] hover:bg-[#3730A3] text-white rounded-sm co-btn-press disabled:opacity-60">
                {saving ? "Saving…" : "Add client"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import report dialog — shown after every CSV upload */}
      <Dialog open={!!importReport} onOpenChange={(v) => !v && setImportReport(null)}>
        <DialogContent className="max-w-2xl" data-testid={T.importReportDialog}>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">
              CSV import report
            </DialogTitle>
            <DialogDescription className="text-sm text-[#52525B]">
              Summary of the last upload with any rows that could not be imported.
            </DialogDescription>
          </DialogHeader>
          {importReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 border co-hairline">
                <div className="p-4 border-r co-hairline">
                  <div className="co-label text-[#15803D]">Imported</div>
                  <div className="font-heading text-3xl font-bold font-mono mt-1">{importReport.inserted}</div>
                </div>
                <div className="p-4">
                  <div className="co-label text-[#B91C1C]">Skipped</div>
                  <div className="font-heading text-3xl font-bold font-mono mt-1">{importReport.errors.length}</div>
                </div>
              </div>

              {importReport.errors.length > 0 && (
                <>
                  <div className="border co-hairline max-h-[300px] overflow-y-auto">
                    <table className="co-table">
                      <thead>
                        <tr>
                          <th style={{ width: 60 }}>Row</th>
                          <th>Name</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importReport.errors.map((e, i) => (
                          <tr key={i} data-testid={T.importReportRow}>
                            <td className="font-mono text-[#52525B]">{e.row}</td>
                            <td className="font-semibold">{e.name || "—"}</td>
                            <td className="text-[#B91C1C] text-xs">
                              <AlertTriangle className="h-3 w-3 inline mr-1" strokeWidth={2} />
                              {e.error}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    onClick={downloadFailedRows}
                    data-testid={T.importDownloadFailed}
                    className="inline-flex items-center gap-2 border co-hairline hover:bg-[#FAFAFA] px-4 py-2 text-sm font-semibold rounded-sm co-btn-press"
                  >
                    <Download className="h-4 w-4 text-[#4338CA]" strokeWidth={1.75} />
                    Download failed rows to fix & re-upload
                  </button>
                </>
              )}

              <DialogFooter>
                <button
                  type="button"
                  onClick={() => setImportReport(null)}
                  data-testid={T.importClose}
                  className="px-4 py-2 text-sm font-semibold bg-[#4338CA] hover:bg-[#3730A3] text-white rounded-sm co-btn-press"
                >
                  Done
                </button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* SMS Template Picker Dialog */}
      <Dialog open={!!smsDialogClient} onOpenChange={(o) => !o && setSmsDialogClient(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={T.smsTemplateDialog}>
          <DialogHeader>
            <DialogTitle>SMS templates for {smsDialogClient?.name}</DialogTitle>
            <DialogDescription>
              Pick a template, copy the SMS and send it from your own phone — no gateway required.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 py-2">
            <div>
              <Label className="text-xs">Template</Label>
              <Select value={smsSelectedTpl || ""} onValueChange={setSmsSelectedTpl}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {smsTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.category} — {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {smsSelectedTpl && smsPreview[smsSelectedTpl] && (
              <>
                <pre
                  data-testid={T.smsRenderedText}
                  className="whitespace-pre-wrap text-[13px] leading-relaxed bg-[#FAFAFA] border co-hairline p-3 rounded-sm font-mono text-[#18181B]"
                >
                  {smsPreview[smsSelectedTpl].text}
                </pre>
                <div className="text-[11px] text-[#71717A]">
                  {smsPreview[smsSelectedTpl].char_count} chars ·{" "}
                  {smsPreview[smsSelectedTpl].segments} SMS segment
                  {smsPreview[smsSelectedTpl].segments > 1 ? "s" : ""}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid={T.smsCopyBtn}
                    onClick={() => copySmsText(smsPreview[smsSelectedTpl].text)}
                    className="co-btn-press inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-[#4338CA] text-white rounded-sm hover:bg-[#3730A3]"
                  >
                    <Copy className="h-4 w-4" strokeWidth={2} />
                    Copy SMS
                  </button>
                  <button
                    type="button"
                    data-testid={T.smsOpenBtn}
                    onClick={() =>
                      openInSmsApp(smsDialogClient, smsPreview[smsSelectedTpl].text)
                    }
                    title="Opens your phone's Messages app with this SMS pre-filled"
                    className="co-btn-press inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border co-hairline rounded-sm hover:bg-[#F4F4F5]"
                  >
                    <ExternalLink className="h-4 w-4" strokeWidth={2} />
                    Open in SMS app
                  </button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Payment Options quick picker */}
      <Dialog open={!!popClient} onOpenChange={(o) => !o && setPopClient(null)}>
        <DialogContent className="sm:max-w-lg" data-testid={T.paymentOptionsPickerDialog}>
          <DialogHeader>
            <DialogTitle>Payment options for {popClient?.name}</DialogTitle>
            <DialogDescription>
              Reminders sent to this client will include ONLY the options you tick below.
              Leave all unchecked to send every active option (default).
            </DialogDescription>
          </DialogHeader>
          <div className="border co-hairline bg-white p-3 space-y-2 max-h-72 overflow-y-auto">
            {paymentOptions.length === 0 ? (
              <div className="text-sm text-[#52525B]">No payment options configured yet. Add one under Payment Options first.</div>
            ) : (
              paymentOptions.map((opt) => {
                const checked = popSelected.includes(opt.id);
                return (
                  <label
                    key={opt.id}
                    className="flex items-center gap-3 cursor-pointer hover:bg-[#FAFAFA] p-2 rounded-sm"
                  >
                    <input
                      type="checkbox"
                      data-testid={`${T.paymentOptionsQuickCheckbox}-${opt.id}`}
                      checked={checked}
                      onChange={(e) => togglePopOption(opt.id, e.target.checked)}
                      className="h-4 w-4 accent-[#4338CA]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{opt.label}</div>
                      <div className="text-[11px] text-[#52525B] truncate">
                        {opt.type === "directory" && (opt.config?.provider_name || "Directory link")}
                        {opt.type === "external" && (opt.config?.url || "External")}
                        {opt.type === "upi" && `UPI · ${opt.config?.upi_id || ""}`}
                        {opt.type === "razorpay" && "Razorpay"}
                        {!opt.active && " · inactive"}
                      </div>
                    </div>
                  </label>
                );
              })
            )}
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setPopClient(null)}
              className="px-4 py-2 text-sm border co-hairline rounded-sm hover:bg-[#F4F4F5]"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid={T.paymentOptionsQuickSave}
              onClick={savePaymentOptions}
              disabled={popSaving}
              className="co-btn-press inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-[#4338CA] text-white rounded-sm hover:bg-[#3730A3] disabled:opacity-60"
            >
              {popSaving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
